// src/mocks/handlers/dashboard/balanceFunding.ts

import { rest } from 'msw';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { IBalanceFundingResponse } from '@/domains/dashboard/balanceFunding/balanceFunding.type';
import balanceFundingData from '@/mocks/data/dashboard_balanceFunding.json';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';
import {
  format,
  subDays,
  subWeeks,
  subMonths,
  startOfWeek,
  startOfMonth,
} from 'date-fns';

export const balanceFundingHandler = rest.get<
  IBalanceFundingResponse,
  { customer_uuid: string }
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.DASHBOARD_BALANCE_FUNDING}`,
  (req, res, ctx) => {
    const { customer_uuid } = req.params;
    const period = req.url.searchParams.get('period');

    // Check if the access token is expired
    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Unauthorized' }));
    }

    // Filter the data based on the customer_uuid
    const customerData = balanceFundingData.find(
      (entry) => entry.uuid === customer_uuid,
    );

    if (!customerData) {
      return res(ctx.status(404), ctx.json({ error: 'Customer not found' }));
    }

    const resultEntry = customerData.periods.find(
      (entry) => entry.period === period,
    );

    if (!resultEntry) {
      return res(
        ctx.status(404),
        ctx.json({ error: 'No balance funding data found' }),
      );
    }

    const today = new Date();
    const updated_at = `${format(subDays(today, 5), 'yyyy-MM-dd')}`;

    const getGeneratedDates = (per: string, count: number): string[] => {
      const base = new Date(updated_at);

      return Array.from({ length: count }, (_, i) => {
        let date: Date;
        if (per === 'daily') {
          date = subDays(base, count - i - 1);
        } else if (per === 'weekly') {
          date = startOfWeek(subWeeks(base, count - i - 1), {
            weekStartsOn: 1,
          });
        } else if (per === 'monthly') {
          date = startOfMonth(subMonths(base, count - i - 1));
        } else {
          date = subDays(base, count - i - 1); // fallback
        }
        return format(date, 'yyyy-MM-dd');
      });
    };

    const periodLengths = {
      daily: 30,
      weekly: 16,
      monthly: 12,
    };

    const dateCount = periodLengths[period as keyof typeof periodLengths] || 12;
    const generatedDates = getGeneratedDates(period ?? 'daily', dateCount);

    const resultsWithDates = resultEntry.results.map((item, idx) => ({
      ...item,
      date: generatedDates[idx] ?? updated_at,
    }));

    const response: IBalanceFundingResponse = {
      results: resultsWithDates,
      currency: customerData.currency,
      updated_at,
    };

    return res(ctx.status(200), ctx.json(response));
  },
);
